import { z } from "zod";
/**
 * Facturation électronique (réception via Pennylane) — epic #646, story #648.
 *
 * MVP = RÉCEPTION seulement (échéance légale 01/09/2026 pour tout assujetti
 * TVA, franchise en base incluse). L'émission est hors scope (#244, 09/2027).
 * Voir `docs/context/facturation.md` § « Facturation électronique » pour les
 * décisions figées.
 */
export const EinvoicingChoiceArray = ["pennylane", "external"];
/**
 * Vocabulaire officiel Pennylane `GET /pa_registrations` (Developer Toolkit
 * RFE, moisson du 10/07/2026) — à réutiliser tel quel, ne pas retraduire :
 * `draft` (enregistrement démarré, pas terminé — seul statut fiable en doc
 * partenaire, `provisioned`/`archived` y sont marqués [DEPRECATED], #1094),
 * `pending` (validation annuaire en cours),
 * `activated` (inscrit à l'annuaire), `conflict`/`ineligible`/`lost` (échecs
 * de désignation, cf. `EinvoicingPennylaneTunnelState.tsx`). `provisioned`
 * reste dans le contrat le temps de la transition (#1098, des lignes
 * existantes le portent encore) ; `archived` n'est jamais observé et n'est
 * pas ajouté. Détail : `docs/context/facturation.md`.
 */
export const PaRegistrationStatusArray = [
    "draft",
    "provisioned",
    "pending",
    "activated",
    "conflict",
    "ineligible",
    "lost",
];
/**
 * Blockers du tunnel, dans l'ordre où le stepper les résout. Le SIRET manquant
 * bloque quel que soit le choix ; `vat_id` n'en fait PAS partie — la TVA est
 * une exigence d'émission, pas de réception (correction de scope, issue #648).
 *
 * Le suivi de la désignation PA se fait entièrement aux credentials
 * partenaire (`GET /companies/pa_registrations`, Provisioning API v1, #651) : aucun blocker
 * OAuth n'est émis, `pennylaneCredentials` ne conditionne rien ici. La
 * colonne et le callback OAuth restent en place, dormants, pour l'émission
 * #244 (2027).
 */
export const EinvoicingBlockerArray = [
    "missingSiret",
    "choiceNotMade",
    "paNotDesignated",
];
/**
 * Numéro de mobile français — format exigé par la Provisioning API Pennylane
 * (`phone_number`, SMS 2FA de finalisation de compte). Accepte les séparateurs
 * courants (espace/point/tiret) ; `06`/`07` ou `+336`/`+337` uniquement — la
 * Provisioning API attend un mobile, pas un fixe.
 */
export const FRENCH_MOBILE_PHONE_REGEX = /^(?:\+33|0)\s*[67](?:[\s.-]?\d{2}){4}$/;
/**
 * Longueur alignée sur les colonnes `varchar(255)` de `company_einvoicing_setting`
 * (external_platform_name, external_routing_code) — sans borne, un payload
 * surdimensionné casse en 500 MySQL au lieu d'un 400 propre. `email` n'a plus besoin
 * de cette borne côté payload : il a quitté `CreatePennylaneAccountPayloadSchema`
 * (#1099), la colonne `pennylane_user_email` reste protégée par construction
 * (source = `platform_user.email`, lui-même `varchar(255)`).
 */
const DB_VARCHAR_MAX = 255;
/**
 * Payload de `POST /api/einvoicing/pennylane-account` (#651). `siret`/
 * `legalName`/`phoneNumber` sont pré-remplis depuis les valeurs serveur mais
 * éditables dans la modal récap — c'est l'étape où l'utilisateur complète/
 * corrige avant transmission à Pennylane (décision #771). `siret`/`legalName`
 * édités sont re-persistés dans `CompanyLegalSetting` au succès de la
 * création (une seule vérité, `EinvoicingService.createPennylaneAccount`).
 *
 * Pas de champ `email` : une erreur de destinataire est irrattrapable
 * (Pennylane n'expose aucun moyen de renvoyer l'invitation, #1094), donc le
 * serveur source l'email du destinataire depuis `ctx.userIdentity.email`
 * plutôt que de faire confiance à un champ de payload modifiable (décision
 * #1099) — uniquement sur la première tentative : un retry réutilise
 * l'email déjà réservé par le manager initiateur, quel que soit qui relance
 * ensuite (#1106).
 */
export const CreatePennylaneAccountPayloadSchema = z.object({
    siret: z
        .string()
        .trim()
        .regex(/^\d{14}$/, "SIRET invalide (14 chiffres attendus)"),
    legalName: z.string().trim().min(1, "Raison sociale requise"),
    phoneNumber: z
        .string()
        .trim()
        .regex(FRENCH_MOBILE_PHONE_REGEX, "Numéro de mobile invalide (format attendu : 06/07 ou +336/+337)"),
});
/**
 * Payload de `POST /api/einvoicing/choice`. Union discriminée sur `choice` :
 *
 * - `"pennylane"` — pas de champ additionnel (le tunnel #651 couvre la
 *   création de compte et la désignation PA ; l'OAuth est hors MVP, réservé
 *   à l'émission #244, 2027).
 * - `"external"` — opt-out simple posé par la modal de nudge (#650), sans
 *   formulaire : `externalPlatformName` est optionnel (simplification UX du
 *   25/07/2026, issue #649) — les colonnes `external_platform_name`/
 *   `external_routing_code` restent en base, nullables, pour un usage futur.
 * - `choice: null` — variant explicite du "revenir sur ce choix" (état opt-out
 *   de #649) : remet le choix à zéro. Pas de champ nullable mou sur `choice`
 *   lui-même ; la remise à zéro est sa propre branche de l'union, au même
 *   titre que les deux autres.
 */
export const UpdateEinvoicingChoicePayloadSchema = z.discriminatedUnion("choice", [
    z.object({ choice: z.literal("pennylane") }),
    z.object({
        choice: z.literal("external"),
        externalPlatformName: z.string().trim().max(DB_VARCHAR_MAX).optional(),
        externalRoutingCode: z.string().trim().max(DB_VARCHAR_MAX).nullable().optional(),
    }),
    z.object({ choice: z.literal(null) }),
]);
/**
 * SIREN = les 9 premiers caractères du SIRET (doctrine dérivé vs stocké —
 * jamais stocké séparément, cf. `docs/context/facturation.md`). `null` si le
 * SIRET n'est pas encore renseigné OU n'a pas un format SIRET valide (14
 * chiffres) — la saisie n'est pas validée en amont (ex. panneau modération),
 * donc mieux vaut n'afficher aucun SIREN qu'un identifiant tronqué trompeur
 * sur un écran de conformité légale.
 */
export function deriveSirenFromSiret(siret) {
    if (!siret || !/^\d{14}$/.test(siret)) {
        return null;
    }
    return siret.slice(0, 9);
}
//# sourceMappingURL=einvoicing.types.js.map